import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';

import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import FormPopup from 'components/Operational/FormPopup';
import Button from 'components/Button';
import Select from 'components/Forms/Fields/Select';
import AsyncSelect from 'components/Forms/Fields/AsyncSelect';

import DetailField from 'components/Details';
import Image from 'components/Image';

import styles from './index.module.scss';
import cn from 'classnames';
import startCase from 'lodash/startCase';
import find from 'lodash/find';
import AddCircleOutlineSharpIcon from '@material-ui/icons/AddCircleOutlineSharp';
import { FORM } from 'utils/constants/css';
import DateTimePicker from 'components/DateTimePicker/DatePicker';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { useTranslation } from 'react-i18next';
import {
  getSecurityCompanyId,
  getSecurityCompanyServices,
  getCompanyAssetCategories,
} from 'reducers/securityCompany';
import API from 'services/api';
import { ReactComponent as DeleteIcon } from 'images/icon_trash.svg';
import { SvgIcon } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    marginBottom: '1rem',
  },
  header: {
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.text.secondary}`,
    marginTop: '0.5rem',
  },
  values: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  value: {
    width: '17%',
    textAlign: 'center',
    padding: '0.5rem 0',
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  btnContainer: {
    width: '75%',
    display: 'flex',
    justifyContent: 'space-evenly',
  },
  img: {
    padding: FORM.PADDING_BUTTON,
    boxSizing: 'border-box',
  },
  fieldSize: {
    width: '90% !important',
    marginBottom: '1rem',
  },
  disable: {
    filter: 'invert(0)',
    opacity: '.50 !important',
  },
  subFormContainerBackground: {
    background: theme.palette.background.default,
  },
  mySvgStyle: {
    height: '25px', //*[@id="Layer_1"]/g/path
    width: '25px',
    cursor: 'pointer',
    '& [id="Layer_1"]': {
      color: `${theme.palette.text.primary} !important`,
      '& [class*="st0"]': {
        fill: `${theme.palette.text.primary} !important`,
      },
    },
  },
}));

const Form = ({ fields, defaultValues, submit, cancel, requiredFields }) => {
  const classes = useStyles();
  const securityCompanyId = useSelector(state => getSecurityCompanyId(state));
  const [assetTypes, updateAssetTypes] = useState([]);
  const [values, setValues] = useState(defaultValues);
  const stateRef = useRef();
  // track the category even if the usestate rerenders
  stateRef.current = values;

  const [touched, setTouched] = useState({});
  const [types, setTypes] = useState(
    fields.reduce(
      (acc, item) => ({
        ...acc,
        [item.field]: item.type,
      }),
      {},
    ),
  );
  const [errors, setErrors] = useState({});

  const setFieldTouched = key => {
    setTouched({ ...touched, [key]: true });
  };
  const handleChange = event => {
    const key = event.target.name;
    const { value } = event.target;
    if (key === 'category' && value === '') {
      setValues(defaultValues);
    } else {
      const item = find(fields, ['field', key]);

      setValues({
        ...values,
        [key]: value,
        typeName: '',
        id: '',
      });
      setTypes({ ...types, [key]: item.type });
    }
  };

  const onAsyncChange = (val, reason) => {
    // pressed cross
    if (reason === 'clear') {
      setValues({
        ...values,
        typeName: '',
        id: '',
      });
      return;
    }

    const foundAsset = val === null ? null : find(assetTypes, { id: val.id });
    setValues({
      ...values,
      id: val ? val.id : '',
      typeName: foundAsset ? foundAsset.name : '',
      category: values.category,
    });
    setTypes({ ...types, typeName: 'text', id: 'text' });
  };
  const setFieldValue = (key, val) => {
    const { type } = find(fields, ['field', key]);
    setTypes({ ...types, [key]: type });
    setValues({
      ...values,
      [key]: val,
    });
  };

  const onAddItem = () => {
    const addedValues = fields.reduce((acc, item) => {
      return {
        ...acc,
        [item.field]: {
          value: values[item.field],
          type: types[item.field],
          ...(item.options &&
            values[item.field] && {
              label: item.options.find(opt => opt.id === values[item.field])
                .name,
            }),
        },
      };
    }, {});
    let tempErrors = {};
    let tempTouched = {};
    //need to refactore below lines
    // eslint-disable-next-line
    requiredFields.map(keyName => {
      if (values[keyName] === '') {
        tempErrors = { ...tempErrors, [keyName]: 'required_label' };
        tempTouched = { ...tempTouched, [keyName]: true };
      }
    });
    setTouched(tempTouched);
    setErrors(tempErrors);
    if (
      Object.keys(tempErrors).length === 0 &&
      Object.keys(tempTouched).length === 0
    ) {
      submit(addedValues);
    }
  };

  const handleDataTransform = ({ data }) => {
    updateAssetTypes(prev => {
      return [...prev, ...data];
    });
    // filter assetTypes based on selected category
    const filteredTypes = data.map(m => ({
      id: m.id,
      typeName: m.name,
    }));
    return filteredTypes;
  };

  const categoryField = fields.find(f => f.field === 'category');

  return (
    <div
      className={cn(styles.formContainer, classes.subFormContainerBackground)}
    >
      <form className={styles.form}>
        <Select
          name={'category'}
          label={startCase('category')}
          className={cn(styles.textInput, classes.fieldSize)}
          required={true}
          options={categoryField?.options ? categoryField?.options : []}
          value={values['category']}
          onChange={handleChange}
          onFocus={() => setFieldTouched('category')}
          error={touched['category'] && errors['category']}
        />
        <AsyncSelect
          name={'id'}
          label={startCase('typeName')}
          required={true}
          disabled={values.category === ''}
          className={cn(styles.textInput, classes.fieldSize)}
          value={values}
          onChange={onAsyncChange}
          fetchOptions={API.getAssetTypeList}
          dataTransform={handleDataTransform}
          fetchFilters={{
            securityCompanyId,
            archived: false,
            category: stateRef.current.category,
          }}
          optionLabelKey={'typeName'}
          onFocus={() => setFieldTouched('id')}
          error={touched['typeName'] && errors['typeName']}
        />

        <DateTimePicker
          className={cn(styles.textInput, classes.fieldSize)}
          onChange={value => {
            setFieldValue('validFrom', value);
          }}
          required={true}
          label={startCase('validFrom')}
          value={values['validFrom']}
          error={touched['validFrom'] && errors['validFrom']}
          showDateTime={false}
        />
        <DateTimePicker
          className={cn(styles.textInput, classes.fieldSize)}
          onChange={value => {
            setFieldValue('validTo', value);
          }}
          required={false}
          label={startCase('validTo')}
          minDate={values['validFrom']}
          value={values['validTo']}
          error={touched['validTo'] && errors['validTo']}
          showDateTime={false}
          endOfDay
        />
        <section className={classes.btnContainer}>
          <Button
            type="button"
            text={'Cancel'}
            onClick={cancel}
            size="l"
            color="secondary"
            outline={true}
          />
          <Button
            type="button"
            onClick={onAddItem}
            text={'Submit'}
            size="l"
            color="primary"
          />
        </section>
      </form>
    </div>
  );
};
Form.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object),
  defaultValues: PropTypes.object.isRequired,
  submit: PropTypes.func.isRequired,
  exception: PropTypes.string,
  cancel: PropTypes.func.isRequired,
  rawAssets: PropTypes.array,
  requiredFields: PropTypes.array,
};
const SubCustomForm = ({
  subFields,
  defaultValues,
  label,
  onChange,
  required,
  value,
  subFormHeader,
  requiredFields,
  disabled,
  info,
  hide,
  services,
}) => {
  const classes = useStyles();
  const [showPopup, setShowPopup] = useState(false);
  const [allFormValues, setallFormValues] = useState(value || []);
  const { t } = useTranslation();
  const onAddItem = () => {
    setShowPopup(true);
  };
  const onClickSubmit = addedValues => {
    setallFormValues([...allFormValues, addedValues]);
    onChange([...allFormValues, addedValues]);
    setShowPopup(false);
  };
  const removeAddedItem = text => {
    const remainingItems = allFormValues.filter(item => item !== text);
    setallFormValues(remainingItems);
    onChange(remainingItems);
  };
  const closePopup = () => {
    setShowPopup(false);
  };
  return (
    <>
      <div
        className={cn(styles.formContainer, classes.subFormContainerBackground)}
      >
        <div className={classes.labelContainer}>
          <InputLabel
            shrink
            required={required}
            disabled={disabled}
            className={cn({ [styles.hide]: hide })}
          >
            {t(label)}
            {info && <Tooltip text={info} />}
          </InputLabel>
          <Image
            {...(!disabled && { onClick: onAddItem })}
            className={cn(
              classes.img,
              { [classes.disable]: disabled },
              { [styles.hide]: hide },
            )}
            disabled={disabled}
            title={subFormHeader}
            icon={<AddCircleOutlineSharpIcon />}
            text={`${t('Add')} ${t(label)}`}
          />
        </div>
        {allFormValues.length > 0 && (
          <section>
            <section className={classes.values}>
              {Object.keys(allFormValues[0]).map((item, key) => {
                return (
                  <div className={cn(classes.value, classes.header)} key={key}>
                    {startCase(item)}
                  </div>
                );
              })}
            </section>
            {allFormValues.map((items, itemIndex) => (
              <section className={classes.values} key={itemIndex}>
                {Object.keys(allFormValues[0]).map((item, index) => {
                  return (
                    <div className={classes.value} key={index}>
                      <DetailField
                        type={items[item].type}
                        value={items[item].label || items[item].value}
                      />
                    </div>
                  );
                })}
                <IconButton
                  color="secondary"
                  component="div"
                  className={classes.icon}
                  onClick={() => removeAddedItem(items)}
                >
                  <SvgIcon className={classes.mySvgStyle}>
                    <DeleteIcon />
                  </SvgIcon>
                </IconButton>
              </section>
            ))}
          </section>
        )}
      </div>
      {showPopup && (
        <FormPopup
          headerTitle={subFormHeader}
          closePopup={closePopup}
          form={
            <Form
              fields={subFields}
              defaultValues={defaultValues}
              submit={onClickSubmit}
              cancel={closePopup}
              value={value}
              requiredFields={requiredFields}
              services={services}
            />
          }
        />
      )}
    </>
  );
};

SubCustomForm.propTypes = {
  subFields: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  defaultValues: PropTypes.object,
  exception: PropTypes.string,
  value: PropTypes.array,
  subFormHeader: PropTypes.string,
  requiredFields: PropTypes.array,
  disabled: PropTypes.bool,
  info: InfoPropTypes,
  hide: PropTypes.bool,
  services: PropTypes.array,
  assetCategories: PropTypes.array,
};
const mapStateToProps = state => ({
  services: getSecurityCompanyServices(state),
  assetCategories: getCompanyAssetCategories(state),
});
export default connect(mapStateToProps)(SubCustomForm);
